import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ScanSearch, Upload, StickyNote, MapPin, UserRound, PackageOpen, Mail, FileDown, FileText, X, ChevronDown } from "lucide-react";

const fmtScan = (iso) => (iso ? new Date(iso).toLocaleString("en-CA", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "");

export default function PRControls() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [people, setPeople] = useState(null);
  const [picked, setPicked] = useState([]);
  const [personQ, setPersonQ] = useState("");
  const [sending, setSending] = useState(false);
  const [savedIds, setSavedIds] = useState([]);
  const [savingList, setSavingList] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [closedOpen, setClosedOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteItems, setQuoteItems] = useState([]);
  const [quoteNotes, setQuoteNotes] = useState("");
  const [quoteBusy, setQuoteBusy] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [scanDiff, setScanDiff] = useState(null);
  const [noteDlg, setNoteDlg] = useState(null);
  const [noteBusy, setNoteBusy] = useState(false);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const r = await api.get("/stores/pr-controls/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `PR_Controls_Summary_${(data?.scanned_at || "").slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF report downloaded");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };
  const fileRef = useRef(null);
  const isAdmin = user?.role === "admin";
  const hasAccess = isAdmin || !!user?.pr_controls_access;

  const load = async () => {
    try {
      const r = await api.get("/stores/pr-controls");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load PR Controls");
    }
  };
  useEffect(() => { if (hasAccess) load(); }, [hasAccess]); // eslint-disable-line react-hooks/exhaustive-deps

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/stores/pr-controls/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Scan complete — ${r.data.count} part requests captured`);
      setSelected([]);
      if (r.data.diff) setScanDiff(r.data.diff);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const grouped = useMemo(() => {
    const byBase = {};
    (data?.requests || []).forEach((r) => {
      const base = r.base || "Unknown base";
      const by = r.requested_by || "Unknown";
      ((byBase[base] = byBase[base] || {})[by] = byBase[base][by] || []).push(r);
    });
    return Object.entries(byBase)
      .map(([base, users]) => ({
        base,
        total: Object.values(users).reduce((n, l) => n + l.length, 0),
        users: Object.entries(users)
          .map(([name, list]) => ({
            name,
            list: [...list].sort((a, b) => ((a.date_requested || "9999") < (b.date_requested || "9999") ? -1 : 1)),
          }))
          .sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => a.base.localeCompare(b.base));
  }, [data]);

  const openEmail = async () => {
    setEmailOpen(true);
    setPersonQ("");
    try {
      const s = await api.get("/stores/pr-controls/schedule");
      setSavedIds(s.data.user_ids || []);
      setPicked(s.data.user_ids || []);
    } catch {
      setPicked([]);
    }
    if (people === null) {
      try {
        const r = await api.get("/admin/users");
        setPeople((r.data || []).filter((p) => (p.email || "").trim()));
      } catch {
        setPeople([]);
      }
    }
  };

  const saveList = async () => {
    setSavingList(true);
    try {
      const r = await api.put("/stores/pr-controls/schedule", { user_ids: picked });
      setSavedIds(r.data.user_ids || []);
      toast.success(`Friday list saved — ${r.data.user_ids.length} recipient${r.data.user_ids.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save list");
    } finally {
      setSavingList(false);
    }
  };

  const sendEmail = async () => {
    if (picked.length === 0) { toast.error("Pick at least one recipient"); return; }
    setSending(true);
    try {
      const r = await api.post("/stores/pr-controls/email", { user_ids: picked });
      toast.success(`Summary emailed to ${r.data.emails_sent} recipient${r.data.emails_sent === 1 ? "" : "s"}`);
      setEmailOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const stats = useMemo(() => {
    const rs = data?.requests || [];
    const withPo = rs.filter((r) => (r.purchases || []).length > 0).length;
    const byBase = {};
    const byUser = {};
    rs.forEach((r) => {
      const b = r.base || "Unknown";
      const u = r.requested_by || "Unknown";
      byBase[b] = (byBase[b] || 0) + 1;
      byUser[u] = (byUser[u] || 0) + 1;
    });
    let last7 = 0;
    if (data?.scanned_at) {
      const cut = new Date(data.scanned_at);
      cut.setDate(cut.getDate() - 7);
      const cutIso = cut.toISOString().slice(0, 10);
      last7 = rs.filter((r) => (r.date_requested || "") >= cutIso).length;
    }
    const closedList = rs.filter((r) => (r.purchases || []).some((p) => (p.po_status || "").toLowerCase() === "closed"));
    return {
      total: rs.length,
      withPo,
      withoutPo: rs.length - withPo,
      last7,
      closed: closedList.length,
      closedList,
      bases: Object.entries(byBase).sort((a, b) => b[1] - a[1]),
      topUsers: Object.entries(byUser).sort((a, b) => b[1] - a[1]).slice(0, 8),
    };
  }, [data]);

  const newNos = useMemo(() => new Set(data?.diff?.added || []), [data]);

  if (!user) return null;
  if (!hasAccess) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="prc-no-access">
        <ScanSearch className="w-8 h-8 mx-auto text-gray-300" />
        <h1 className="font-display text-2xl font-semibold mt-4">PR Controls</h1>
        <p className="text-sm text-gray-500 mt-2">Ask an admin to grant you PR Controls access.</p>
      </div>
    );
  }

  const parsePart = (part) => {
    const segs = (part || "").split(" - ").map((s) => s.trim()).filter(Boolean);
    if (segs.length >= 3) return { description: segs.slice(1, -1).join(" - "), part_number: segs[segs.length - 1] };
    if (segs.length === 2) return { description: segs[0], part_number: segs[1] };
    return { description: part || "", part_number: "" };
  };

  const openQuote = () => {
    const rs = (data?.requests || []).filter((r) => selected.includes(r.request_no));
    setQuoteItems(rs.map((r) => ({ ...parsePart(r.part), qty: r.qty || "" })));
    setQuoteNotes("");
    setQuoteOpen(true);
  };

  const markQuoted = async (nos) => {
    if (!nos.length) return;
    try {
      await api.post("/stores/pr-controls/mark-quoted", { request_nos: nos });
      load();
    } catch {}
  };

  const copyEmailDraft = async () => {
    const lines = quoteItems.map((it, i) => `${i + 1}. ${it.description || "—"} — P/N ${it.part_number || "—"} — Qty: ${it.qty || "—"}`);
    const draft = [
      "Subject: Request for Quote — Summit Air Stores",
      "",
      "Hello,",
      "",
      "Could you please provide pricing and lead time for the following items:",
      "",
      ...lines,
      ...(quoteNotes.trim() ? ["", `Notes: ${quoteNotes.trim()}`] : []),
      "",
      "Thank you,",
      `${user?.first_name || ""} ${user?.last_name || ""}`.trim(),
      "Summit Air Stores",
    ].join("\n");
    try {
      await navigator.clipboard.writeText(draft);
      toast.success("Email draft copied — paste it into your email");
      markQuoted(selected);
    } catch {
      toast.error("Couldn't copy to clipboard");
    }
  };

  const saveNote = async () => {
    setNoteBusy(true);
    try {
      await api.put("/stores/pr-controls/note", { request_no: noteDlg.request_no, note: noteDlg.note });
      toast.success((noteDlg.note || "").trim() ? "Note saved" : "Note cleared");
      setNoteDlg(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save note");
    } finally {
      setNoteBusy(false);
    }
  };

  const downloadQuote = async () => {
    setQuoteBusy(true);
    try {
      const r = await api.post(
        "/stores/pr-controls/quote-pdf",
        { items: quoteItems, notes: quoteNotes || null },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Quote_Request_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Quote request PDF downloaded");
      markQuoted(selected);
      setQuoteOpen(false);
      setSelected([]);
    } catch {
      toast.error("Failed to generate quote request");
    } finally {
      setQuoteBusy(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="prc-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Stores · MST</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight mt-1" data-testid="prc-heading">PR Controls</h1>
          {data?.scanned_at ? (
            <p className="font-display text-lg font-semibold text-summit-primary mt-2" data-testid="prc-asof">
              Open / Pending Part Request as of - {fmtScan(data.scanned_at)}
            </p>
          ) : (
            <p className="text-sm text-gray-500 mt-2" data-testid="prc-asof-empty">No scan yet — upload a RAAS Part Request printout to get started.</p>
          )}
          {data?.scanned_at && (
            <p className="font-mono text-[10px] uppercase tracking-widest text-gray-400 mt-1">
              {data.count} requests · scanned by {data.scanned_by_name} · {data.filename}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".pdf" className="hidden" onChange={onFile} data-testid="prc-scan-input" />
          {data?.scanned_at && (
            <>
              <Button variant="outline" className="border-summit-hairline" disabled={exporting} onClick={exportPdf} data-testid="prc-export-btn">
                <FileDown className="w-4 h-4 mr-1.5" /> {exporting ? "Exporting…" : "Export PDF"}
              </Button>
              {isAdmin && (
                <Button variant="outline" className="border-summit-hairline" onClick={openEmail} data-testid="prc-email-btn">
                  <Mail className="w-4 h-4 mr-1.5" /> Email Summary
                </Button>
              )}
            </>
          )}
          <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" disabled={scanning} onClick={() => fileRef.current?.click()} data-testid="prc-scan-btn">
            <Upload className="w-4 h-4 mr-1.5" /> {scanning ? "Scanning…" : "Scan PDF"}
          </Button>
        </div>
      </div>

      <div className="mt-6 flex gap-6 items-start">
        {data !== null && stats.total > 0 && (
          <aside className="hidden lg:block w-56 shrink-0 sticky top-24" data-testid="prc-summary-panel">
            <div className="border border-summit-hairline rounded-lg bg-white p-4 shadow-sm">
              <div className="font-mono text-[9px] uppercase tracking-widest text-gray-400">Summary</div>
              <div className="font-display text-2xl font-semibold mt-1" data-testid="prc-summary-total">{stats.total}</div>
              <div className="font-mono text-[9px] uppercase tracking-widest text-gray-500 -mt-0.5">open part requests</div>

              <div className="mt-3">
                <div className="flex items-center justify-between font-mono text-[9px] uppercase tracking-widest text-gray-500">
                  <span>New (last 7 days)</span>
                  <span className="text-sky-700 font-bold" data-testid="prc-summary-last7">{stats.last7}</span>
                </div>
                <div className="flex items-center justify-between font-mono text-[9px] uppercase tracking-widest text-gray-500 mt-1">
                  <span>With PO</span>
                  <span className="text-emerald-700 font-bold" data-testid="prc-summary-withpo">{stats.withPo}</span>
                </div>
                <div className="flex items-center justify-between font-mono text-[9px] uppercase tracking-widest text-gray-500 mt-1">
                  <span>No PO</span>
                  <span className="text-amber-700 font-bold" data-testid="prc-summary-nopo">{stats.withoutPo}</span>
                </div>
                <button
                  type="button"
                  className="w-full flex items-center justify-between font-mono text-[9px] uppercase tracking-widest text-gray-500 mt-1 hover:text-emerald-700"
                  onClick={() => setClosedOpen(true)}
                  title="Click to see the affected part requests"
                  data-testid="prc-summary-closed-btn"
                >
                  <span className="underline decoration-dotted underline-offset-2">Closed PO</span>
                  <span className="text-emerald-700 font-bold" data-testid="prc-summary-closed">{stats.closed}</span>
                </button>
                <div className="mt-1.5 h-2 rounded-full bg-amber-200 overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${stats.total ? Math.round((stats.withPo / stats.total) * 100) : 0}%` }} />
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-summit-hairline">
                <div className="font-mono text-[9px] uppercase tracking-widest text-gray-400">By base</div>
                <div className="mt-1.5 space-y-1" data-testid="prc-summary-bases">
                  {stats.bases.map(([b, n]) => (
                    <div key={b} className="flex items-center justify-between gap-2 text-[11px]">
                      <span className="truncate text-gray-600" title={b}>{b}</span>
                      <span className="font-mono font-bold text-summit-primary shrink-0">{n}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-summit-hairline">
                <div className="font-mono text-[9px] uppercase tracking-widest text-gray-400">Top requestors</div>
                <div className="mt-1.5 space-y-1" data-testid="prc-summary-requestors">
                  {stats.topUsers.map(([u2, n]) => (
                    <div key={u2} className="flex items-center justify-between gap-2 text-[11px]">
                      <span className="truncate font-mono text-gray-600" title={u2}>{u2}</span>
                      <span className="px-1.5 py-0.5 rounded-full bg-summit-primary/10 text-summit-primary font-mono text-[10px] font-bold shrink-0">{n}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        )}
        <div className="flex-1 min-w-0 space-y-6">
        {data === null && <p className="text-sm text-gray-400">Loading…</p>}
        {data !== null && grouped.length === 0 && (
          <div className="text-center text-gray-400 py-14 border border-dashed border-summit-hairline rounded-lg" data-testid="prc-empty">
            <PackageOpen className="w-8 h-8 mx-auto text-gray-300" />
            <p className="text-sm mt-3">Nothing here yet — scan a Part Request printout PDF.</p>
          </div>
        )}
        {grouped.map((g) => (
          <div key={g.base} className="border border-summit-hairline rounded-lg bg-white overflow-hidden" data-testid={`prc-base-${g.base}`}>
            <button
              type="button"
              className="w-full bg-summit-primary text-white px-4 py-2 flex items-center gap-2"
              onClick={() => setCollapsed((m) => ({ ...m, [g.base]: !m[g.base] }))}
              data-testid={`prc-base-toggle-${g.base}`}
            >
              <ChevronDown className={`w-4 h-4 transition-transform ${collapsed[g.base] ? "-rotate-90" : ""}`} />
              <MapPin className="w-3.5 h-3.5" />
              <span className="font-display font-semibold">{g.base}</span>
              <span className="ml-auto font-mono text-[10px] uppercase tracking-widest opacity-80" data-testid={`prc-base-count-${g.base}`}>{g.total} request{g.total === 1 ? "" : "s"}</span>
            </button>
            {!collapsed[g.base] && (
            <div className="divide-y divide-summit-hairline">
              {g.users.map((u) => (
                <div key={u.name} data-testid={`prc-user-${g.base}-${u.name}`}>
                  <div className="px-4 py-1.5 bg-summit-canvas/60 flex items-center gap-2">
                    <UserRound className="w-3.5 h-3.5 text-gray-500" />
                    <span className="font-mono text-xs font-semibold">{u.name}</span>
                    <span className="px-1.5 py-0.5 rounded-full bg-summit-primary/10 text-summit-primary font-mono text-[10px] font-bold" data-testid={`prc-user-count-${u.name}`}>{u.list.length}</span>
                  </div>
                  <div className="divide-y divide-summit-hairline/60">
                    {u.list.map((r) => (
                      <div key={r.request_no} className="px-4 py-1.5 flex items-center gap-3 text-xs hover:bg-summit-glacier/30" data-testid={`prc-row-${r.request_no}`}>
                        <Checkbox
                          className="shrink-0"
                          checked={selected.includes(r.request_no)}
                          onCheckedChange={(v) => setSelected((cur) => (v ? [...cur, r.request_no] : cur.filter((x) => x !== r.request_no)))}
                          data-testid={`prc-select-${r.request_no}`}
                        />
                        <span className="font-mono font-bold text-summit-primary shrink-0 w-14">#{r.request_no}</span>
                        {newNos.has(r.request_no) && (
                          <span className="shrink-0 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-mono text-[9px] uppercase tracking-widest" data-testid={`prc-new-${r.request_no}`}>New</span>
                        )}
                        <span className="truncate flex-1 min-w-0" title={r.part}>{r.part || "—"}</span>
                        <span className="font-mono text-gray-600 shrink-0 w-16 text-right">{r.qty || "—"}</span>
                        <span className="font-mono text-gray-500 shrink-0 w-20" title="Date requested">{r.date_requested || "—"}</span>
                        <span className="shrink-0 flex items-center gap-1 w-52 justify-end flex-wrap">
                          {(r.purchases || []).length === 0 && <span className="px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-400 font-mono text-[9px] uppercase tracking-widest">No PO</span>}
                          {(r.purchases || []).map((p, i) => (
                            <span key={i} className={`px-1.5 py-0.5 rounded-full font-mono text-[9px] uppercase tracking-widest ${p.po_status === "Sent" ? "bg-sky-100 text-sky-800" : p.po_status === "Closed" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`} title={`Qty ${p.qty || "—"} · PO ${p.po || "—"} · ${p.po_status || "—"} · Item ${p.item || "—"} ${p.item_status || ""}`} data-testid={`prc-po-${r.request_no}-${i}`}>
                              PO {p.po || "—"}{p.po_status ? ` · ${p.po_status}` : ""}{p.item_status ? ` · ${p.item_status}` : ""}
                            </span>
                          ))}
                        </span>
                        {r.quoted_at && (
                          <span
                            className="shrink-0 px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 font-mono text-[9px] uppercase tracking-widest cursor-help"
                            title={`Quoted by ${r.quoted_by_name || "—"} · ${(r.quoted_at || "").slice(0, 10)}`}
                            data-testid={`prc-quoted-${r.request_no}`}
                          >
                            Quoted {(r.quoted_at || "").slice(0, 10)}
                          </span>
                        )}
                        {r.notes ? (
                          <span className="shrink-0 cursor-help" title={r.notes} data-testid={`prc-notes-${r.request_no}`}>
                            <StickyNote className="w-3.5 h-3.5 text-amber-500" />
                          </span>
                        ) : (
                          <span className="shrink-0 w-3.5" />
                        )}
                        <button
                          type="button"
                          className="shrink-0"
                          onClick={() => setNoteDlg({ request_no: r.request_no, note: r.user_note || "" })}
                          title={r.user_note ? `${r.user_note} — ${r.user_note_by || ""}` : "Add your note to this PR"}
                          data-testid={`prc-user-note-btn-${r.request_no}`}
                        >
                          <StickyNote className={`w-3.5 h-3.5 ${r.user_note ? "text-sky-500" : "text-gray-300 hover:text-sky-500"}`} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            )}
          </div>
        ))}
        </div>
      </div>

      {/* Email summary dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="prc-email-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Mail className="w-4 h-4" /> Email summary</DialogTitle>
            <DialogDescription>Recipients get the summary (totals, new in last 7 days, PO breakdown, by base, top requestors) plus a PDF with all part requests grouped by requestor. Saved lists also receive it automatically every Friday 8:00 AM (MST).</DialogDescription>
          </DialogHeader>
          <Input className="h-9 text-sm" placeholder="Filter people…" value={personQ} onChange={(e) => setPersonQ(e.target.value)} data-testid="prc-email-filter" />
          <div className="border border-summit-hairline rounded-md divide-y divide-summit-hairline/60 max-h-64 overflow-y-auto" data-testid="prc-email-list">
            {people === null && <p className="text-sm text-gray-400 p-3">Loading…</p>}
            {(people || [])
              .filter((p) => `${p.last_name || ""} ${p.first_name || ""} ${p.email || ""}`.toLowerCase().includes(personQ.trim().toLowerCase()))
              .map((p) => (
                <label key={p.id} className="flex items-center gap-2.5 px-3 py-1.5 text-sm cursor-pointer hover:bg-summit-glacier/30">
                  <Checkbox
                    checked={picked.includes(p.id)}
                    onCheckedChange={(v) => setPicked((cur) => (v ? [...cur, p.id] : cur.filter((x) => x !== p.id)))}
                    data-testid={`prc-email-user-${p.id}`}
                  />
                  <span className="font-mono text-xs">{p.last_name}, {p.first_name}</span>
                  <span className="ml-auto text-[10px] text-gray-400 truncate max-w-[160px]">{p.email}</span>
                </label>
              ))}
          </div>
          <div className="flex items-center justify-between gap-2 border border-summit-hairline rounded-md px-3 py-2 bg-summit-canvas/40">
            <div className="min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Friday auto-email</div>
              <div className="text-xs text-gray-600" data-testid="prc-schedule-count">{savedIds.length === 0 ? "No saved recipients yet" : `${savedIds.length} saved recipient${savedIds.length === 1 ? "" : "s"} · every Friday 8:00 AM MST`}</div>
            </div>
            <Button size="sm" variant="outline" className="shrink-0 h-8" disabled={savingList} onClick={saveList} data-testid="prc-schedule-save">
              {savingList ? "Saving…" : "Save selection as Friday list"}
            </Button>
          </div>
          <Button className="w-full bg-summit-primary hover:bg-summit-primary-soft text-white" disabled={sending || picked.length === 0} onClick={sendEmail} data-testid="prc-email-send">
            {sending ? "Sending…" : `Send to ${picked.length || "…"} recipient${picked.length === 1 ? "" : "s"}`}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Closed-PO part requests dialog */}
      <Dialog open={closedOpen} onOpenChange={setClosedOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="prc-closed-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <PackageOpen className="w-4 h-4 text-emerald-600" /> Closed PO part requests
            </DialogTitle>
            <DialogDescription>{stats.closed} request{stats.closed === 1 ? "" : "s"} with a closed PO in the current scan</DialogDescription>
          </DialogHeader>
          <div className="divide-y divide-summit-hairline">
            {stats.closedList.map((r) => (
              <div key={r.request_no} className="py-2 flex items-center gap-3 text-xs" data-testid={`prc-closed-row-${r.request_no}`}>
                <span className="font-mono font-bold text-summit-primary shrink-0 w-14">#{r.request_no}</span>
                <span className="truncate flex-1 min-w-0" title={r.part}>{r.part || "—"}</span>
                <span className="font-mono text-gray-500 shrink-0">{r.base}</span>
                <span className="font-mono text-gray-500 shrink-0 truncate max-w-[110px]" title={r.requested_by}>{r.requested_by}</span>
                <span className="shrink-0 flex items-center gap-1 flex-wrap justify-end">
                  {(r.purchases || []).filter((p) => (p.po_status || "").toLowerCase() === "closed").map((p, i) => (
                    <span key={i} className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-mono text-[9px] uppercase tracking-widest">PO {p.po || "—"}</span>
                  ))}
                </span>
              </div>
            ))}
            {stats.closed === 0 && <p className="py-4 text-sm text-gray-400" data-testid="prc-closed-empty">No closed-PO requests in this scan.</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Multi-select quote request bar */}
      {selected.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 bg-summit-primary text-white rounded-full shadow-lg px-5 py-2.5" data-testid="prc-selection-bar">
          <span className="font-mono text-[11px] uppercase tracking-widest">{selected.length} selected</span>
          <Button size="sm" className="h-7 rounded-full bg-white text-summit-primary hover:bg-summit-glacier" onClick={openQuote} data-testid="prc-quote-btn">
            <FileText className="w-3.5 h-3.5 mr-1" /> Quote request
          </Button>
          <button type="button" className="font-mono text-[10px] uppercase tracking-widest opacity-80 hover:opacity-100 underline" onClick={() => setSelected([])} data-testid="prc-selection-clear">
            Clear
          </button>
        </div>
      )}

      {/* Quote request dialog */}
      <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="prc-quote-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <FileText className="w-4 h-4 text-summit-primary" /> Quote request
            </DialogTitle>
            <DialogDescription>Auto-populated from the selected part requests — edit anything before downloading.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="grid grid-cols-[1fr_170px_90px_28px] gap-2 font-mono text-[9px] uppercase tracking-widest text-gray-400 px-0.5">
              <span>Description</span><span>Part number</span><span>Qty</span><span />
            </div>
            {quoteItems.map((it, i) => (
              <div key={i} className="grid grid-cols-[1fr_170px_90px_28px] gap-2 items-center" data-testid={`prc-quote-item-${i}`}>
                <Input className="h-8 text-xs" value={it.description} onChange={(e) => setQuoteItems((cur) => cur.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))} data-testid={`prc-quote-desc-${i}`} />
                <Input className="h-8 text-xs font-mono" value={it.part_number} onChange={(e) => setQuoteItems((cur) => cur.map((x, j) => (j === i ? { ...x, part_number: e.target.value } : x)))} data-testid={`prc-quote-pn-${i}`} />
                <Input className="h-8 text-xs" value={it.qty} onChange={(e) => setQuoteItems((cur) => cur.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))} data-testid={`prc-quote-qty-${i}`} />
                <button type="button" className="text-gray-300 hover:text-rose-500" onClick={() => setQuoteItems((cur) => cur.filter((_, j) => j !== i))} data-testid={`prc-quote-remove-${i}`}>
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            <Input className="h-8 text-xs" placeholder="Notes for the supplier (optional)" value={quoteNotes} onChange={(e) => setQuoteNotes(e.target.value)} data-testid="prc-quote-notes" />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1 border-summit-hairline" disabled={quoteItems.length === 0} onClick={copyEmailDraft} data-testid="prc-quote-copy-email">
              <Mail className="w-4 h-4 mr-1.5" /> Copy email draft
            </Button>
            <Button className="flex-1 bg-summit-primary hover:bg-summit-primary-soft text-white" disabled={quoteBusy || quoteItems.length === 0} onClick={downloadQuote} data-testid="prc-quote-download">
              <FileDown className="w-4 h-4 mr-1.5" /> {quoteBusy ? "Generating…" : "Download PDF"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Per-line user note dialog */}
      <Dialog open={!!noteDlg} onOpenChange={(v) => !v && setNoteDlg(null)}>
        <DialogContent className="max-w-md" data-testid="prc-note-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <StickyNote className="w-4 h-4 text-sky-500" /> Note — PR #{noteDlg?.request_no}
            </DialogTitle>
            <DialogDescription>Your note sticks to this PR number across rescans. Clear the text to remove it.</DialogDescription>
          </DialogHeader>
          <Input
            className="h-9 text-sm"
            value={noteDlg?.note || ""}
            onChange={(e) => setNoteDlg((d) => ({ ...d, note: e.target.value }))}
            placeholder="e.g. quote sent to Acme, waiting on reply"
            data-testid="prc-note-input"
          />
          <Button className="w-full bg-summit-primary hover:bg-summit-primary-soft text-white" disabled={noteBusy} onClick={saveNote} data-testid="prc-note-save">
            {noteBusy ? "Saving…" : "Save note"}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Scan diff dialog */}
      <Dialog open={!!scanDiff} onOpenChange={(v) => !v && setScanDiff(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="prc-diff-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Scan changes</DialogTitle>
            <DialogDescription>
              Compared to the previous scan ({scanDiff?.prev_count ?? 0} requests): +{(scanDiff?.added || []).length} new · −{(scanDiff?.removed || []).length} removed
            </DialogDescription>
          </DialogHeader>
          {(scanDiff?.added || []).length > 0 && (
            <div data-testid="prc-diff-added">
              <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-700">+ New requests</div>
              <div className="mt-1 flex flex-wrap gap-1">
                {(scanDiff?.added || []).map((no) => (
                  <span key={no} className="px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 font-mono text-[10px]">#{no}</span>
                ))}
              </div>
            </div>
          )}
          {(scanDiff?.removed || []).length > 0 && (
            <div data-testid="prc-diff-removed">
              <div className="font-mono text-[10px] uppercase tracking-widest text-rose-700">− No longer on the list</div>
              <div className="mt-1 divide-y divide-summit-hairline/60">
                {(scanDiff?.removed || []).map((r) => (
                  <div key={r.request_no} className="py-1 flex items-center gap-2 text-xs">
                    <span className="font-mono font-bold text-rose-700 shrink-0">#{r.request_no}</span>
                    <span className="truncate flex-1">{r.part || "—"}</span>
                    <span className="font-mono text-gray-400 shrink-0">{r.base || ""}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(scanDiff?.added || []).length === 0 && (scanDiff?.removed || []).length === 0 && (
            <p className="text-sm text-gray-400" data-testid="prc-diff-none">No changes since the previous scan.</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
